import { supabase } from "@/integrations/supabase/client";

const KEY = "tl_session_id";

// Boty/search crawlery renderujące JS potrafią generować realne wpisy w bazie.
// Nie są potrzebne w Live panelu, a przy crawlach SEO tworzą zbędny koszt DB.
const AUTOMATED_UA_RE = /(bot|crawler|spider|headlesschrome|puppeteer|playwright|phantomjs|chrome-lighthouse|pagespeed|google-inspectiontool|googleother|adsbot-google|storebot-google|bingpreview|facebookexternalhit|slackbot|telegrambot|whatsapp|discordbot|ahrefs|semrush|mj12|dotbot|petalbot|gptbot|chatgpt-user|oai-searchbot|claudebot|claude-web|perplexitybot|bytespider)/i;

function isAutomatedVisitor(): boolean {
  if (typeof navigator === "undefined") return false;
  return AUTOMATED_UA_RE.test(navigator.userAgent || "");
}

export function getSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
  }
  return id;
}

// Globalna „ochrona przed dubletami" — gdy klik wywoła i onClick i onAuxClick,
// albo gdy ktoś dwukrotnie kliknie ten sam wynik, nie chcemy podwójnych wpisów.
const recentOutbound = new Map<string, number>();

export function trackOutboundEsky(targetUrl: string, price?: number, currency?: string) {
  if (typeof window === "undefined") return;
  if (isAutomatedVisitor()) return;
  const now = Date.now();
  const last = recentOutbound.get(targetUrl);
  if (last && now - last < 1500) return;
  recentOutbound.set(targetUrl, now);

  const priceLabel =
    typeof price === "number" && price > 0
      ? `${Math.round(price)} ${(currency ?? "PLN").toUpperCase()}`
      : null;

  const payload = {
    session_id: getSessionId(),
    path: "/out/esky",
    referrer: window.location.href,
    user_agent: navigator.userAgent,
    utm_campaign: targetUrl.slice(0, 500),
    utm_term: priceLabel,
  };

  // UWAGA: nie używamy `navigator.sendBeacon` — Beacon z Content-Type
  // `application/json` jest cross-origin po cichu blokowany przez przeglądarki
  // (wymagałby CORS preflight, którego Beacon nie potrafi wysłać). Funkcja
  // zwraca `true`, ale request nigdy nie dociera do Supabase. To była przyczyna
  // braku zliczeń klików w „Wybierz".
  //
  // `fetch` z `keepalive: true` przeżywa natychmiastową nawigację / zamknięcie
  // karty TAK SAMO jak Beacon, a jednocześnie poprawnie obsługuje CORS preflight
  // i pozwala ustawić nagłówki (`apikey`, `Authorization`, `Content-Type`).
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/analytics_events`;
    const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    void fetch(url, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        apikey,
        Authorization: `Bearer ${apikey}`,
        Prefer: "return=minimal",
      },
      body: JSON.stringify(payload),
    }).catch(() => {
      // Last-resort fallback przez SDK (pełny klient Supabase). Bez `keepalive`,
      // ale lepsze niż nic gdy fetch został zablokowany przez extension.
      void supabase.from("analytics_events").insert(payload);
    });
  } catch {
    void supabase.from("analytics_events").insert(payload);
  }
}

// — Live „state" wyszukiwarki: pokazuje w adminie ile wyników widzi user
// w danym momencie, czy szukamy, czy wywaliło się błędem. Wysyłamy do tej
// samej tabeli `analytics_events`, ale na sztuczną ścieżkę `/_state/search`,
// którą admin filtruje ze statystyk (żeby nie inflate'owało top paths /
// last24h). Stan trzymamy w `utm_campaign` jako mały JSON (≤500 znaków).
//
// Throttling: wysyłamy tylko gdy payload się zmienił, ale nie częściej niż
// raz na ~2.5s, oraz force'ujemy refresh raz na 15s żeby wpis nie „starzał"
// się w adminie gdy user siedzi na liście wyników bez zmian.
type SearchState = {
  visible: number;
  status: "loading" | "results" | "empty" | "error";
  route?: string;
  pairs?: number;
  errorMsg?: string | null;
  // Ostrzeżenie partial-success — gdy część par tras / kombinacji dat
  // padła, ale mamy wyniki z reszty. Admin pokazuje to live w karcie
  // sesji, żebyśmy widzieli to samo co user.
  partial?: string | null;
  // Info, że user widzi badge ograniczenia („sprawdzamy 30 z 42 wariantów
  // dat" / „sprawdzamy 400 par lotnisk"). Kompaktowy string, np.
  // "30/42 dat · 400/1200 par" — admin renderuje 1:1.
  truncated?: string | null;
};
let lastSearchStatePayload: string | null = null;
let lastSearchStateSentAt = 0;
let lastSearchStateStatus: SearchState["status"] | null = null;
let lastSearchStateRoute: string | undefined = undefined;
let pendingFlush: ReturnType<typeof setTimeout> | null = null;
let pendingState: SearchState | null = null;

function flushSearchState(state: SearchState) {
  if (isAutomatedVisitor()) return;
  const payload = JSON.stringify(state);
  lastSearchStatePayload = payload;
  lastSearchStateSentAt = Date.now();
  lastSearchStateStatus = state.status;
  lastSearchStateRoute = state.route;
  pendingState = null;
  if (pendingFlush) { clearTimeout(pendingFlush); pendingFlush = null; }

  const body = {
    session_id: getSessionId(),
    path: "/_state/search",
    referrer: window.location.href,
    user_agent: navigator.userAgent,
    utm_campaign: payload.slice(0, 500),
  };
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/analytics_events`;
    const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    void fetch(url, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        apikey,
        Authorization: `Bearer ${apikey}`,
        Prefer: "return=minimal",
      },
      body: JSON.stringify(body),
    }).catch(() => {
      void supabase.from("analytics_events").insert(body);
    });
  } catch {
    void supabase.from("analytics_events").insert(body);
  }
}

export function trackSearchState(state: SearchState) {
  if (typeof window === "undefined") return;
  const payload = JSON.stringify(state);
  const now = Date.now();
  const changed = payload !== lastSearchStatePayload;
  const aged = now - lastSearchStateSentAt > 15_000;
  // Trasa / status / błąd to "twarde" przejścia — admin MUSI je widzieć
  // natychmiast (inaczej widzi stary stan innego lotu / "loading" gdy
  // user widzi już wyniki). Pomijamy throttle.
  const statusChanged = state.status !== lastSearchStateStatus;
  const routeChanged = state.route !== lastSearchStateRoute;
  const isTerminal = state.status === "results" || state.status === "empty" || state.status === "error";
  const force = statusChanged || routeChanged || isTerminal;

  if (!changed && !aged) return;
  if (changed && !force && now - lastSearchStateSentAt < 2_500 && !aged) {
    // Trailing edge — zapamiętujemy najnowszy stan i wysyłamy po końcu okna,
    // żeby admin nigdy nie utknął na pośrednim "loading 0".
    pendingState = state;
    if (!pendingFlush) {
      const wait = Math.max(0, 2_500 - (now - lastSearchStateSentAt));
      pendingFlush = setTimeout(() => {
        pendingFlush = null;
        if (pendingState) flushSearchState(pendingState);
      }, wait + 50);
    }
    return;
  }
  flushSearchState(state);
}

// Pozwala wymusić wysyłkę ostatniego stanu (np. przy unmount / pagehide),
// żeby admin nie widział "loading" gdy user już wyszedł z wynikami.
export function flushPendingSearchState() {
  if (typeof window === "undefined") return;
  if (pendingState) flushSearchState(pendingState);
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => flushPendingSearchState(), { capture: true });
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushPendingSearchState();
  });
}

// — Eventy „Gdziekolwiek": chcemy w adminie widzieć,
//   1) kiedy ktoś wybiera intencję „Anywhere" (jaką frazą / skąd kliknął),
//   2) jakie kierunki potem klika z listy wyników.
// Trzymamy je jako lekkie eventy z dedykowaną ścieżką `/_event/anywhere-*`,
// admin filtruje je z top-paths (żeby nie zaśmiecały statystyk) i pokazuje
// w osobnej karcie.
type AnywhereIntent = {
  // Skąd przyszedł sygnał: picker (klik w opcję), keyword (autosugestia po
  // wpisaniu „wszędzie" itd.), hub (klik na stronie /gdziekolwiek), nav
  // (link w menu/stopce).
  source: "picker" | "keyword" | "hub" | "nav";
  // Co user wpisał (jeśli dotyczy). Maks 80 znaków.
  query?: string;
  // Lotnisko źródłowe wybrane przy intencji (jeśli już znane).
  origin?: string;
};
export function trackAnywhereIntent(intent: AnywhereIntent) {
  if (typeof window === "undefined") return;
  const payload = JSON.stringify({
    source: intent.source,
    query: intent.query ? intent.query.slice(0, 80) : undefined,
    origin: intent.origin,
  });
  sendEvent("/_event/anywhere-intent", payload);
}

type AnywhereClick = {
  origin: string;       // np. "WAW", "WAR", "XPL"
  destination: string;  // kod IATA / metro / country
  country?: string;     // ISO/PL nazwa kraju (do podglądu w adminie)
  price?: number;       // cena w PLN
};
export function trackAnywhereClick(click: AnywhereClick) {
  if (typeof window === "undefined") return;
  const payload = JSON.stringify({
    origin: click.origin,
    destination: click.destination,
    country: click.country,
    price: click.price,
  });
  sendEvent("/_event/anywhere-click", payload);
}

// — Motyw (dark/light) wybrany przez usera. Pushujemy do `/_state/theme`,
// admin agreguje per session_id i pokazuje w karcie live (czy user siedzi
// na ciemnym czy jasnym). Wysyłamy tylko gdy się zmienił.
let lastThemeSent: "dark" | "light" | null = null;
export function trackTheme(theme: "dark" | "light") {
  if (typeof window === "undefined") return;
  if (lastThemeSent === theme) return;
  lastThemeSent = theme;
  sendEvent("/_state/theme", JSON.stringify({ theme }));
}

// — Monitoring „zdrowia" wyszukiwarki: pchamy do `/_event/search-cancel`
// i `/_event/search-restart`, żeby admin widział na żywo, kiedy fan-outy
// padają / są przerywane przez usera / restartowane przez F5. To są
// dedykowane eventy (nie state), więc NIE używamy throttla z `trackSearchState`
// — chcemy widzieć każdy pojedynczy incydent.
//
// `reason` rozróżnia źródło anulowania:
//  - "user"       → klik „Anuluj" w UI
//  - "autoswitch" → user przeszedł na inną trasę (zmienił cancelKey w URL),
//                   stare in-flight zapytania są ucinane
//  - "unmount"    → komponent /search został zdjęty (nawigacja na inną stronę)
type SearchCancelReason = "user" | "autoswitch" | "unmount";
export function trackSearchCancel(opts: { reason: SearchCancelReason; route?: string; inFlight: number }) {
  if (typeof window === "undefined") return;
  // Nawet jeśli inFlight === 0, logujemy — to jest sygnał, że user kliknął
  // anuluj na pustym/skończonym wyszukiwaniu (też informacja diagnostyczna).
  sendEvent(
    "/_event/search-cancel",
    JSON.stringify({ reason: opts.reason, route: opts.route, inFlight: opts.inFlight }),
  );
}

// Restart = drugi (i kolejny) start tej samej trasy w krótkim oknie.
// Najczęstsza przyczyna: hard-reload (F5) w trakcie ładowania. Każdy taki
// restart zabija postęp po stronie klienta i zmusza serwer do dołączenia
// do (lub odpalenia od nowa) cache'owanego Promise.
export function trackSearchRestart(opts: { route?: string; ageMs: number; trigger: "reload" | "navigation" }) {
  if (typeof window === "undefined") return;
  sendEvent(
    "/_event/search-restart",
    JSON.stringify({ route: opts.route, ageMs: Math.round(opts.ageMs), trigger: opts.trigger }),
  );
}

// Kliknięcie „Szukaj" w widgecie Travelpayouts — admin pokazuje licznik
// dziś/wczoraj (dzień liczony od 5:00 czasu warszawskiego).
export function trackSearchSubmit() {
  if (typeof window === "undefined") return;
  sendEvent("/_event/search-submit", JSON.stringify({ page: window.location.pathname }));
}

// Wspólny sender — fetch keepalive z fallbackiem na SDK.
function sendEvent(path: string, payloadJson: string) {
  if (isAutomatedVisitor()) return;
  const body = {
    session_id: getSessionId(),
    path,
    referrer: window.location.href,
    user_agent: navigator.userAgent,
    utm_campaign: payloadJson.slice(0, 500),
  };
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/analytics_events`;
    const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    void fetch(url, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        apikey,
        Authorization: `Bearer ${apikey}`,
        Prefer: "return=minimal",
      },
      body: JSON.stringify(body),
    }).catch(() => {
      void supabase.from("analytics_events").insert(body);
    });
  } catch {
    void supabase.from("analytics_events").insert(body);
  }
}

export async function trackPageview(path: string) {
  if (typeof window === "undefined") return;
  if (isAutomatedVisitor()) return;
  const params = new URLSearchParams(window.location.search);
  // Logujemy przez server function, żeby wzbogacić wpis o IP/kraj/język
  // (z nagłówków HTTP — `cf-connecting-ip`, `cf-ipcountry`, `accept-language`).
  // Bezpośredni insert z klienta nie ma do tych danych dostępu.
  try {
    const { logPageview } = await import("@/lib/log-pageview.functions");
    await logPageview({
      data: {
        session_id: getSessionId(),
        path,
        referrer: document.referrer || null,
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
        utm_term: params.get("utm_term"),
        utm_content: params.get("utm_content"),
        user_agent: navigator.userAgent,
      },
    });
  } catch {
    // Fallback: jeśli server function padł (np. w dev SSR), logujemy bez IP.
    await supabase.from("analytics_events").insert({
      session_id: getSessionId(),
      path,
      referrer: document.referrer || null,
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      utm_term: params.get("utm_term"),
      utm_content: params.get("utm_content"),
      user_agent: navigator.userAgent,
    });
  }
}
